const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:3000";
const API_PREFIX = "/api/v1";

const TOKEN_KEY = "nudge_admin_token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = getToken();

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((options.headers as Record<string, string>) || {}),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${API_PREFIX}${path}`, {
    ...options,
    headers,
  });

  // Handle token refresh
  const refreshedToken = res.headers.get("X-Refreshed-Token");
  if (refreshedToken) {
    setToken(refreshedToken);
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = body.error || {};
    throw new ApiError(
      res.status,
      err.code || "UNKNOWN",
      err.message || "Request failed",
    );
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

const api = {
  get<T>(path: string): Promise<T> {
    return apiFetch<T>(path);
  },
  post<T>(path: string, body?: unknown): Promise<T> {
    return apiFetch<T>(path, {
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
    });
  },
  patch<T>(path: string, body?: unknown): Promise<T> {
    return apiFetch<T>(path, {
      method: "PATCH",
      body: body ? JSON.stringify(body) : undefined,
    });
  },
  upload<T>(path: string, formData: FormData): Promise<T> {
    const token = getToken();
    const headers: Record<string, string> = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    // Don't set Content-Type — browser sets multipart boundary
    return fetch(`${API_BASE}${API_PREFIX}${path}`, {
      method: "POST",
      headers,
      body: formData,
    }).then(async (res) => {
      const refreshedToken = res.headers.get("X-Refreshed-Token");
      if (refreshedToken) setToken(refreshedToken);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const err = body.error || {};
        throw new ApiError(
          res.status,
          err.code || "UNKNOWN",
          err.message || "Request failed",
        );
      }
      return res.json();
    });
  },
};

// ─── Auth ────────────────────────────────────────────────────────────────────

interface AuthResponse {
  token: string;
  user: {
    id: number;
    name: string;
    email: string;
    roles: string[];
  };
}

export const authApi = {
  login(data: { email: string; password: string }) {
    return api.post<AuthResponse>("/auth/login", data);
  },
  me() {
    return api.get<{ user: AuthResponse["user"] }>("/auth/me");
  },
};

// ─── Admin ───────────────────────────────────────────────────────────────────

interface CronJob {
  job: string;
  lastRun: {
    id: number;
    status: string;
    triggeredBy: string;
    startedAt: string;
    finishedAt: string | null;
    durationMs: number | null;
    result: unknown;
    errorMessage: string | null;
  } | null;
}

export interface AdminDashboard {
  dateRange?: { from: string; to: string; prevFrom: string; prevTo: string };
  users: {
    dau: number;
    dauPrevious: number;
    wau: number;
    wauPrevious: number;
    mau: number;
    mauPrevious: number;
    totalUsers: number;
    guestVsAuth: { guest: number; auth: number };
  };
  period: {
    signups: number;
    signupsPrevious: number;
    interactions: number;
    interactionsPrevious: number;
    contactsCreated: number;
    contactsCreatedPrevious: number;
    referrals: number;
    referralsPrevious: number;
    notificationsSent: number;
    notificationsSentPrevious: number;
    apiErrors: number;
    apiErrorsPrevious: number;
    avgInteractionsPerUser: number;
    totalActiveContacts: number;
  };
  support: {
    openTickets: number;
    needsAttention: number;
    avgResponseTimeMinutes: number | null;
    recentTickets: Array<{
      id: number;
      userEmail: string;
      topic: string;
      status: string;
      createdAt: string;
    }>;
  };
  system: {
    dbPool: { active: number; idle: number; total: number } | null;
    redis: { connected: boolean; memoryUsedMb: number } | null;
    cronJobs: Array<{
      jobName: string;
      status: string;
      lastRunAt: string | null;
      durationMs: number | null;
      error: string | null;
    }>;
  };
  growth: {
    totalReferrals: number;
    referralConversion: number;
    topReferrers: Array<{ name: string; count: number }>;
  };
  push: {
    enabledUsers: number;
  };
}

export const adminApi = {
  dashboard(from?: string, to?: string) {
    const params = new URLSearchParams();
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    const qs = params.toString();
    return api.get<AdminDashboard>(`/admin/dashboard${qs ? `?${qs}` : ""}`);
  },
  cronStatus() {
    return api.get<{ jobs: CronJob[] }>("/admin/cron-status");
  },
  cronTrigger(job: string) {
    return api.post<{ status: string; message?: string; result?: unknown }>(
      "/admin/cron-trigger",
      {
        job,
      },
    );
  },
  cronHistory(jobName: string, limit = 10) {
    return api.get<{ data: CronJob["lastRun"][] }>(
      `/admin/cron-history/${jobName}?limit=${limit}`,
    );
  },
  listUsers() {
    return api.get<{ data: AdminUser[] }>("/admin/users");
  },
  sendNotification(data: { user_id: number; title: string; body: string }) {
    return api.post<{ message: string }>("/admin/send-notification", data);
  },
  supportTickets(status?: string, limit = 50, offset = 0) {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    params.set("limit", String(limit));
    params.set("offset", String(offset));
    return api.get<{
      data: AdminSupportTicket[];
      pagination: {
        total: number;
        limit: number;
        offset: number;
        hasMore: boolean;
      };
    }>(`/admin/support?${params}`);
  },
  updateTicketStatus(ticketId: number, status: string, adminNotes?: string) {
    return api.patch<AdminSupportTicket>(`/admin/support/${ticketId}`, {
      status,
      admin_notes: adminNotes,
    });
  },
  ticketMessages(ticketId: number) {
    return api.get<{
      ticket: AdminSupportTicket;
      messages: AdminTicketMessage[];
    }>(`/admin/support/${ticketId}/messages`);
  },
  referrals() {
    return api.get<ReferralOverview>("/admin/referrals");
  },
  errors(opts?: {
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (opts?.from) params.set("from", opts.from);
    if (opts?.to) params.set("to", opts.to);
    if (opts?.limit) params.set("limit", String(opts.limit));
    if (opts?.offset) params.set("offset", String(opts.offset));
    const qs = params.toString();
    return api.get<{
      data: AdminApiError[];
      pagination: {
        total: number;
        limit: number;
        offset: number;
        hasMore: boolean;
      };
    }>(`/admin/errors${qs ? `?${qs}` : ""}`);
  },
  addTicketMessage(ticketId: number, message: string, attachments?: File[]) {
    const formData = new FormData();
    formData.append("message", message);
    if (attachments) {
      attachments.forEach((file) => formData.append("attachments", file));
    }
    return api.upload<AdminTicketMessage>(
      `/admin/support/${ticketId}/messages`,
      formData,
    );
  },
};

export interface AdminSupportTicket {
  id: number;
  userId: number;
  topic: string;
  message: string;
  attachments: string[];
  status: string;
  adminNotes: string | null;
  resolvedAt: string | null;
  createdAt: string;
  userName: string;
  userEmail: string;
}

export interface AdminTicketMessage {
  id: number;
  ticketId: number;
  senderType: "user" | "admin";
  senderId: number;
  message: string;
  attachments: string[];
  createdAt: string;
  senderName?: string;
}

export interface AdminUser {
  id: number;
  name: string;
  email: string;
  pushToken: string | null;
  timezone: string | null;
  referralCode: string;
  referralCount: number;
  roles: string[];
  createdAt: string;
}

export interface AdminApiError {
  id: number;
  statusCode: number;
  method: string;
  path: string;
  errorMessage: string;
  errorCode: string | null;
  stackTrace: string | null;
  requestBody: string | null;
  queryParams: string | null;
  userId: number | null;
  userEmail: string | null;
  createdAt: string;
}

export interface ReferralOverview {
  totalReferrals: number;
  topReferrers: {
    userId: number;
    name: string;
    email: string;
    count: number;
  }[];
  recentReferrals: {
    referrerName: string;
    referredName: string;
    createdAt: string;
  }[];
}
